import { action, flow, makeObservable, observable } from 'mobx';

import { isPrimitive } from 'src/packages/types';
import { hasValue } from 'src/packages/utils/has-value';

import type { TComboBoxView, TControlView, TMultiComboBoxView } from './types/controls-views.type';
import type { TRefQuery } from '../directories-storage-service/types';
import type { NotificationsService } from '../notifications-service';
import type { TOption } from 'src/packages/types';

import { mapComboboxOptionsConcatinationRule } from '../control-rules-service/mappers';
// eslint-disable-next-line max-len
import { handleComboboxOptionsConcatinationRule } from '../control-rules-service/rule-handlers/handle-combobox-options-concatination-rule';
import {
  REF_QUERY_REG_EXP,
  type IDirectoriesStorage,
  type TStorageObject,
} from '../directories-storage-service/directories-storage-service';

type TDeps = {
  fieldId: string[];
  attrs: string[];
  outer: string[];
};

export const getDynamicJoinDependencies = (refQuery: TRefQuery): TDeps => {
  const dependencies: TDeps = {
    fieldId: [],
    attrs: [],
    outer: [],
  };

  const stringifiedRefQuery = JSON.stringify(refQuery);

  const values = Array.from(stringifiedRefQuery.matchAll(REF_QUERY_REG_EXP));

  for (const value of values) {
    const type = value[1];
    const valueKey = value[2];
    switch (type) {
      // Values are obtained from controls
      case '$': {
        dependencies.attrs.push(`$${valueKey}`);
        break;
      }
      // Values are obtained from directories
      case '%': {
        dependencies.fieldId.push(`%${valueKey}`);
        break;
      }
      //  Values are obtained from outer object of global variables
      case '@': {
        dependencies.outer.push(`@${valueKey}`);
        break;
      }
    }
  }

  return dependencies;
};

type TCreateOptionsFromObjectsFn = (
  objects: TStorageObject<Record<string, unknown>>[],
  attrName: string | null,
  refObjectValueAttr: string | null,
  getLabel?: (obj: TStorageObject<Record<string, unknown>>) => string,
) => {
  options: TOption<string | number>[];
  archivedOptions: TOption<string | number>[];
  optionsMap: Map<number | string, TOption<string | number>>;
  archivedOptionsMap: Map<number | string, TOption<string | number>>;
};

const createOptionsFromObjects: TCreateOptionsFromObjectsFn = (objects, attrName, refObjectValueAttr, getLabel) => {
  const options: TOption<string | number>[] = [];
  const archivedOptions: TOption<string | number>[] = [];
  const optionsMap = new Map<number | string, TOption<string | number>>();
  const archivedOptionsMap = new Map<number | string, TOption<string | number>>();

  for (const object of objects) {
    const isArchived = object.status === 'DELETED';

    const label = ((): string | null => {
      if (getLabel) {
        return getLabel(object);
      }

      if (attrName) {
        const labelValue = object.data[attrName];

        if (isPrimitive(labelValue)) {
          return labelValue.toString();
        }

        return null;
      }

      return null;
    })();

    if (!label) {
      continue;
    }

    const optionValue = (() => {
      if (refObjectValueAttr) {
        const value = object.data[refObjectValueAttr];

        if (typeof value !== 'number' && typeof value !== 'string') {
          console.warn(`invalid value for type: ${refObjectValueAttr}, value: ${value}`);
          return object.id;
        }

        return value;
      }

      return object.id;
    })();

    const option = { label, value: optionValue };

    if (isArchived) {
      archivedOptions.push(option);
      archivedOptionsMap.set(optionValue, option);
    } else {
      options.push(option);
      optionsMap.set(optionValue, option);
    }
  }

  return { options, archivedOptions, optionsMap, archivedOptionsMap };
};

export class OptionsService {
  private readonly directoriesStorageService: IDirectoriesStorage;
  private readonly notificationsService: NotificationsService;

  @observable private objectSubscriptionDisposers: Record<string, VoidFunction> = {};
  @observable.shallow readonly options: Record<string, TOption<string | number>[]> = {};
  @observable.shallow readonly archivedOptions: Record<string, TOption<string | number>[]> = {};
  readonly optionsMap: Record<string, Map<number | string, TOption<string | number>>> = {};
  readonly archivedOptionsMap: Record<string, Map<number | string, TOption<string | number>>> = {};

  constructor(notificationsService: NotificationsService, directoriesStorageService: IDirectoriesStorage) {
    this.notificationsService = notificationsService;
    this.directoriesStorageService = directoriesStorageService;
    makeObservable(this);
  }

  @action.bound
  createInitialOptions(controlsViews: TControlView[]): void {
    for (const control of controlsViews) {
      if ('predefinedOptions' in control) {
        if (control.predefinedOptions?.length) {
          this.setOptions(
            control.fieldId,
            control.predefinedOptions,
            new Map(control.predefinedOptions.map((option) => [option.value, option])),
          );
          continue;
        }
      }

      if ('refObjectType' in control) {
        if ('optionsConcatination' in control && !!control.optionsConcatination) {
          const objectType = control.refObjectType;

          if (this.options[objectType ?? '']?.length) {
            continue;
          }

          const objects = this.directoriesStorageService.directories[objectType ?? ''] ?? [];
          const concatinationRule = mapComboboxOptionsConcatinationRule({
            concatinationView: control.optionsConcatination,
          });

          const updateOptions = (objects: TStorageObject[]) => {
            const getLabel = (obj: TStorageObject<Record<string, unknown>>): string => {
              return handleComboboxOptionsConcatinationRule(
                this.directoriesStorageService,
                {
                  ...obj.data,
                  id: obj.id,
                },
                concatinationRule.rule,
              ).value;
            };

            const { options, archivedOptions, optionsMap, archivedOptionsMap } = createOptionsFromObjects(
              objects,
              null,
              control.refObjectValueAttr ?? null,
              getLabel,
            );

            this.setOptions(control.fieldId, options, optionsMap);
            this.setArchivedOptions(control.fieldId, archivedOptions, archivedOptionsMap);
          };

          updateOptions(objects);

          // Subscribe to the list of objects in the desired directory in order to respond to changes in the composition of this list
          const disposer = this.directoriesStorageService.subscribeOnObjectsChange(objectType ?? '', updateOptions);

          this.objectSubscriptionDisposers[objectType ?? ''] = disposer;

          continue;
        }

        if ('refObjectAttr' in control) {
          const objectAttr = control.refObjectAttr;
          const objectType = control.refObjectType;

          if (this.options[objectType ?? '']?.length) {
            continue;
          }

          const updateOptions = (objects: TStorageObject[]) => {
            const { options, archivedOptions, optionsMap, archivedOptionsMap } = createOptionsFromObjects(
              objects,
              objectAttr ?? null,
              control.refObjectValueAttr ?? null,
            );
            this.setOptions(control.fieldId, options, optionsMap);
            this.setArchivedOptions(control.fieldId, archivedOptions, archivedOptionsMap);
          };

          const objects = this.directoriesStorageService.directories[objectType ?? ''] ?? [];
          updateOptions(objects);

          // Subscribe to the list of objects in the desired directory in order to respond to changes in the composition of this list
          const disposer = this.directoriesStorageService.subscribeOnObjectsChange(objectType ?? '', updateOptions);

          this.objectSubscriptionDisposers[objectType ?? ''] = disposer;
        }
      }
    }
  }

  @flow.bound
  async *getOptionsByRefQuery(
    controlView: TComboBoxView | TMultiComboBoxView,
    data: Record<string, unknown>,
    signal?: AbortSignal,
  ): Promise<TOption[]> {
    const refQuery = controlView.refQuery;

    if (!refQuery) {
      console.warn('ref query is not presented');
      return [];
    }

    const options: TOption[] = [];

    try {
      const dependencies = getDynamicJoinDependencies(refQuery);
      const dependenciesObject: Record<string, unknown> = {};

      for (const dep of dependencies.fieldId) {
        const key = dep.slice(1);
        const value = data[key];

        if (hasValue(value)) {
          dependenciesObject[dep] = value;
        }
      }

      // TODO: зачем нам dependencies.attrs?
      for (const dep of dependencies.attrs) {
        const key = dep.slice(1);
        const value = data[key];

        if (hasValue(value)) {
          dependenciesObject[dep] = value;
        }
      }

      const objectsArr = await this.directoriesStorageService.loadObjectsByRefQuery<Record<string, unknown>>(
        refQuery,
        dependenciesObject,
        signal,
      );
      yield;

      if (!objectsArr) {
        return [];
      }

      for (const objects of objectsArr) {
        const mainObject = objects?.[controlView.refObjectType ?? ''];

        if (!mainObject) {
          continue;
        }

        const value = mainObject.id;

        if ('concatination' in controlView) {
          // TODO: обработать конкатенацию
        }

        const label = (() => {
          if (controlView.optionsConcatination) {
            const concatinationRule = mapComboboxOptionsConcatinationRule({
              concatinationView: controlView.optionsConcatination,
            });
            const getLabel = (obj: TStorageObject<Record<string, unknown>>): string => {
              return handleComboboxOptionsConcatinationRule(
                this.directoriesStorageService,
                {
                  ...obj.data,
                  id: obj.id,
                },
                concatinationRule.rule,
              ).value;
            };

            return getLabel(mainObject);
          }

          return mainObject.data[controlView.refObjectAttr ?? ''];
        })();

        if (isPrimitive(label)) {
          options.push({
            label: label.toString(),
            value,
          });
        } else {
          console.warn(`invalid label for type: ${controlView.refObjectType}, attr: ${controlView.refObjectAttr}`);
        }
      }

      return options;
    } catch (e) {
      yield;
      console.error(e);
    }

    return options;
  }

  @action.bound
  setOptions(
    attrName: string,
    options: TOption<string | number>[],
    optionsMap: Map<number | string, TOption<string | number>>,
  ): void {
    this.options[attrName] = options;
    this.optionsMap[attrName] = optionsMap;
  }

  @action.bound
  setArchivedOptions(
    attrName: string,
    options: TOption<string | number>[],
    archivedOptionsMap: Map<number | string, TOption<string | number>>,
  ): void {
    this.archivedOptions[attrName] = options;
    this.archivedOptionsMap[attrName] = archivedOptionsMap;
  }

  init = (): VoidFunction => {
    return () => {
      Object.values(this.objectSubscriptionDisposers).forEach((disposer) => disposer());
    };
  };
}
